/* platform/PlatformUserDetail — IAM detail for a single platform user.
   Route: /platform/users/:userId */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { StatCard, TabBar, TenancyModal } from './_ui';
import { getPlatformRole, can, PLATFORM_ROLES } from './rbac.js';
import * as puApi from '../api/platform-users.api.js';
import * as authApi from '../api/auth.api.js';
import * as auditApi from '../api/audit.api.js';
import * as rolesApi from '../api/platform-roles.api.js';

const ROLE_OPTIONS = Object.entries(PLATFORM_ROLES)
  .filter(([v]) => v !== 'SUPER_ADMIN')
  .map(([value, info]) => ({ value, label: info.label }));

const TABS = [
  { id: 'overview',     label: 'Overview',     icon: 'Home'        },
  { id: 'profile',      label: 'Profile',      icon: 'User'        },
  { id: 'permissions',  label: 'Permissions',  icon: 'ShieldCheck' },
  { id: 'sessions',     label: 'Sessions',     icon: 'Monitor'     },
  { id: 'audit',        label: 'Audit',        icon: 'Activity'    },
];

const SYSTEM_PERMISSIONS = {
  SUPER_ADMIN:    ['*'],
  PLATFORM_ADMIN: ['Organization.*','Workspace.*','License.*','Partner.*','PlatformUser.*','Role.*','Audit.Read','Analytics.Read','Settings.*','SystemHealth.Read','Notification.*','Search.Read'],
  SUPPORT:        ['Organization.Read','Workspace.Read','PlatformUser.Read','PlatformUser.Update','Audit.Read','SystemHealth.Read','Notification.Read','Search.Read','Analytics.Read'],
  BILLING:        ['Organization.Read','Workspace.Read','License.Read','License.Write','Analytics.Read','Notification.Read','Search.Read'],
  AUDITOR:        ['Organization.Read','Workspace.Read','License.Read','Partner.Read','Role.Read','PlatformUser.Read','Audit.Read','Analytics.Read','Notification.Read','Search.Read'],
};

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const InfoRow = ({ label, value }) => (
  <div className="nx-form-row"><label>{label}</label><span>{value || '—'}</span></div>
);

export default function PlatformUserDetail() {
  const { userId }  = useParams();
  const navigate    = useNavigate();
  const toast       = useToast();
  const myRole      = getPlatformRole();
  const canWrite    = can(myRole, 'write');
  const isSA        = myRole === 'SUPER_ADMIN';

  const [user, setUser]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('overview');
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileBusy, setProfileBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editRole, setEditRole]   = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [customRoles, setCustomRoles]   = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    puApi.getOne(userId)
      .then(u => { setUser(u); setProfileForm({ name: u.name, phone: u.phone || '', department: u.department || '', notes: u.notes || '' }); })
      .catch(() => toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load user' }))
      .finally(() => setLoading(false));
  }, [userId, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== 'audit' || !user) return;
    setAuditLoading(true);
    auditApi.list({ limit: 200 })
      .then(res => {
        const all = Array.isArray(res) ? res : (res?.data ?? []);
        const email = user.email || '';
        const uid   = user.id    || '';
        setAuditEvents(all.filter(e =>
          e.who === email ||
          e.target === email ||
          e.target === uid
        ));
      })
      .catch(() => setAuditEvents([]))
      .finally(() => setAuditLoading(false));
  }, [tab, user]);

  const updateLocal = (patch) => setUser(u => ({ ...u, ...patch }));

  const [suspendConfirm, setSuspendConfirm] = useState(false);

  const handleSuspend = async () => {
    setSuspendConfirm(false);
    try {
      await puApi.deactivate(user.id);
      updateLocal({ status: 'SUSPENDED' });
      toast({ tone: 'warning', icon: 'AlertCircle', title: user.name + ' suspended', desc: 'Role and account preserved.' });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: err?.message }); }
  };

  const handleReactivate = async () => {
    try {
      await puApi.activate(user.id);
      updateLocal({ status: 'ACTIVE' });
      toast({ tone: 'success', icon: 'Check', title: user.name + ' reactivated' });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: err?.message }); }
  };

  const handleForceLogout = async () => {
    try {
      await puApi.forceLogout(user.id);
      toast({ tone: 'warning', icon: 'LogOut', title: 'All sessions terminated', desc: user.name });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err?.message }); }
  };

  const handleResetPassword = async () => {
    try {
      await authApi.forgotPassword(user.email);
      toast({ tone: 'success', icon: 'Key', title: 'Reset link sent', desc: user.email });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err?.message }); }
  };

  const handleSaveProfile = async () => {
    setProfileBusy(true);
    try {
      await puApi.updateProfile(user.id, profileForm);
      updateLocal(profileForm);
      setEditProfile(false);
      toast({ tone: 'success', icon: 'Check', title: 'Profile updated' });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Update failed', desc: err?.message }); }
    finally { setProfileBusy(false); }
  };

  const openRoleModal = () => {
    rolesApi.list()
      .then(res => setCustomRoles(Array.isArray(res) ? res.filter(r => !r.isSystem) : []))
      .catch(() => setCustomRoles([]));
    setSelectedRole(user.platformRole || '');
    setEditRole(true);
  };

  const handleRoleChange = async () => {
    try {
      const isCustom = customRoles.some(r => r.id === selectedRole);
      if (isCustom) {
        await puApi.assignRole(user.id, selectedRole);
        const cr = customRoles.find(r => r.id === selectedRole);
        updateLocal({ platformCustomRoleId: selectedRole, platformCustomRole: cr || null });
      } else {
        await puApi.updateRole(user.id, selectedRole);
        updateLocal({ platformRole: selectedRole, platformCustomRoleId: null, platformCustomRole: null });
      }
      setEditRole(false);
      toast({ tone: 'success', icon: 'ShieldCheck', title: 'Role updated' });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Role update failed', desc: err?.message }); }
  };

  const handleDelete = async () => {
    setDeleteConfirm(false);
    try {
      await puApi.revoke(user.id);
      // Update local state — user account is retained, just no platform role.
      updateLocal({ platformRole: null, platformCustomRoleId: null, platformCustomRole: null });
      toast({ tone: 'warning', icon: 'ShieldOff', title: 'Access removed', desc: user.name + ' — account retained, no platform role.' });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err?.message }); }
  };

  if (loading) return (
    <div className="nx-cc-page">
      <button className="nx-cc-back" onClick={() => navigate('/platform/users')}><NexusIcon name="ChevronLeft" size={14} /> Back</button>
      <div className="nx-cc-skeleton" style={{ height: 300, marginTop: 16 }} />
    </div>
  );

  if (!user) return (
    <div className="nx-cc-page">
      <button className="nx-cc-back" onClick={() => navigate('/platform/users')}><NexusIcon name="ChevronLeft" size={14} /> Back</button>
      <div className="nx-empty" style={{ marginTop: 40 }}><NexusIcon name="User" size={24} /><span>User not found.</span></div>
    </div>
  );

  const isActive    = (user.status || '').toUpperCase() === 'ACTIVE';
  const permissions = SYSTEM_PERMISSIONS[user.platformRole] || (user.platformCustomRole?.permissions) || [];
  const sessions    = user.refreshTokens || [];

  let auditContent;
  if (auditLoading) {
    auditContent = (
      <div className="nx-empty" style={{ padding: '32px 0' }}>
        <NexusIcon name="Loader" size={20} />
        <span>Loading audit events…</span>
      </div>
    );
  } else if (auditEvents.length === 0) {
    auditContent = (
      <div className="nx-empty" style={{ padding: '32px 0' }}>
        <NexusIcon name="Activity" size={20} />
        <span>No audit events found for this user.</span>
      </div>
    );
  } else {
    auditContent = (
      <div className="nx-table-wrap">
        <table className="nx-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Category</th>
              <th>Target</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {auditEvents.map((e, i) => (
              <tr key={e.id || i}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {e.createdAt ? new Date(e.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </td>
                <td>{e.action || '—'}</td>
                <td>{e.cat || '—'}</td>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.target || '—'}</td>
                <td>{e.ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="nx-cc-page">
      <button className="nx-cc-back" onClick={() => navigate('/platform/users')}>
        <NexusIcon name="ChevronLeft" size={14} /> Back to Platform Users
      </button>

      {/* Hero */}
      <div className="nx-cc-org-hero">
        <div className="nx-cc-org-logo" style={{ background: '#6E3FBA18', color: '#6E3FBA', fontSize: 18 }}>
          {(user.name || user.email || 'U').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
        </div>
        <div className="nx-cc-org-meta">
          <h2 className="nx-cc-page-title">{user.name}</h2>
          <div className="nx-cc-org-meta-row">
            <span className="nx-muted nx-mini">{user.email}</span>
            {/* Canonical status display */}
            {!user.platformRole && !user.platformCustomRole
              ? <Pill tone="slate">No Access</Pill>
              : isActive
                ? <Pill tone="green">Active</Pill>
                : <Pill tone="amber">Suspended</Pill>}
            {user.platformRole && <Pill tone="purple">{(user.platformRole || '').replaceAll('_', ' ')}</Pill>}
            {user.platformCustomRole && <Pill tone="blue">{user.platformCustomRole.name}</Pill>}
            {!user.platformRole && !user.platformCustomRole && <Pill tone="slate">Unassigned</Pill>}
            {user.department && <span className="nx-muted nx-mini">{user.department}</span>}
          </div>
        </div>
        {isSA && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn variant="ghost" size="sm" icon="Key"    onClick={handleResetPassword}>Reset Password</Btn>
            <Btn variant="ghost" size="sm" icon="LogOut" onClick={handleForceLogout}>Force Logout</Btn>
            {/* Suspend / Reactivate — only meaningful when user has a role */}
            {(user.platformRole || user.platformCustomRole) && isActive && (
              <Btn variant="ghost" size="sm" icon="AlertCircle" data-testid="pud-suspend-btn"
                onClick={() => setSuspendConfirm(true)}>
                Suspend Access
              </Btn>
            )}
            {!isActive && (
              <Btn variant="ghost" size="sm" icon="Check" data-testid="pud-reactivate-btn"
                onClick={handleReactivate}>
                Reactivate
              </Btn>
            )}
            {/* Assign / Change Role */}
            {(user.platformRole || user.platformCustomRole)
              ? <Btn variant="ghost" size="sm" icon="Shield" onClick={openRoleModal}>Change Role</Btn>
              : <Btn variant="ghost" size="sm" icon="Shield" data-testid="pud-assign-btn" onClick={openRoleModal}>Assign Role</Btn>}
            {/* Remove Access only when there is a role to remove */}
            {(user.platformRole || user.platformCustomRole) && (
              <Btn variant="ghost" size="sm" icon="ShieldOff" data-testid="pud-remove-btn"
                onClick={() => setDeleteConfirm(true)}>
                Remove Access
              </Btn>
            )}
          </div>
        )}
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      <div className="nx-cc-tab-body">

        {/* OVERVIEW */}
        {tab === 'overview' && (
          <div>
            <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 16 }}>
              <StatCard label="Status"       value={(user.status || 'active').toLowerCase()} icon="Activity"    color={isActive ? '#0E8C5A' : '#C26A1F'} />
              <StatCard label="Role"         value={(user.platformRole || user.platformCustomRole?.name || '—').replace(/_/g, ' ')} icon="ShieldCheck" color="#6E3FBA" />
              <StatCard label="Last Login"   value={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString('en-IN', { day:'numeric', month:'short' }) : 'Never'} icon="LogIn" color="#125AB0" />
              <StatCard label="Active Sessions" value={sessions.length} icon="Monitor" color="#E08A00" />
            </div>
            <div className="nx-form" style={{ maxWidth: 540 }}>
              <InfoRow label="User ID"    value={<span className="nx-cell is-mono" style={{ fontSize: 11 }}>{user.id}</span>} />
              <InfoRow label="Email"      value={user.email} />
              <InfoRow label="Phone"      value={user.phone} />
              <InfoRow label="Department" value={user.department} />
              <InfoRow label="Timezone"   value={user.timezone} />
              <InfoRow label="Created"    value={fmt(user.createdAt)} />
              <InfoRow label="Last Login" value={fmt(user.lastLoginAt)} />
              <InfoRow label="Updated"    value={fmt(user.updatedAt)} />
              {user.notes && <InfoRow label="Notes" value={user.notes} />}
            </div>
          </div>
        )}

        {/* PROFILE — edit form */}
        {tab === 'profile' && (
          <div style={{ maxWidth: 540 }}>
            <div className="nx-form">
              {[
                ['Name',       'name',       'text',  'Display name'],
                ['Phone',      'phone',      'tel',   '+91 9876543210'],
                ['Department', 'department', 'text',  'Engineering'],
              ].map(([label, field, type, ph]) => (
                <div key={field} className="nx-form-row">
                  <label>{label}</label>
                  <input className="nx-input" type={type} placeholder={ph}
                    value={profileForm[field] ?? ''}
                    disabled={!canWrite}
                    onChange={e => setProfileForm(f => ({ ...f, [field]: e.target.value }))} />
                </div>
              ))}
              <div className="nx-form-row">
                <label>Notes</label>
                <textarea className="nx-input" rows={3} disabled={!canWrite}
                  value={profileForm.notes ?? ''}
                  onChange={e => setProfileForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            {canWrite && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Btn variant="primary" icon="Save" onClick={handleSaveProfile} disabled={profileBusy}>
                  {profileBusy ? 'Saving…' : 'Save Profile'}
                </Btn>
              </div>
            )}
          </div>
        )}

        {/* PERMISSIONS */}
        {tab === 'permissions' && (
          <div>
            <div className="nx-cc-chart-card">
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">Effective Permissions</span>
                {user.platformRole && <Pill tone="purple">{user.platformRole.replace(/_/g, ' ')}</Pill>}
                {user.platformCustomRole && <Pill tone="blue">Custom: {user.platformCustomRole.name}</Pill>}
              </div>
              {permissions.includes('*') ? (
                <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--nx-danger-tint, rgba(220,38,38,0.08))', borderRadius: 6, color: '#DC2626', fontSize: 13, fontWeight: 600 }}>
                  Super Admin — all permissions granted (*)
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                  {permissions.map(p => (
                    <Pill key={p} tone="blue">{p}</Pill>
                  ))}
                  {permissions.length === 0 && <span className="nx-muted nx-mini">No permissions assigned.</span>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SESSIONS */}
        {tab === 'sessions' && (
          <div>
            <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(2,1fr)', marginBottom: 16 }}>
              <StatCard label="Active Sessions" value={sessions.length} icon="Monitor" color="#125AB0" />
              <StatCard label="Force Logout"    value="Revoke all"     icon="LogOut"  color="#C26A1F" onClick={isSA ? handleForceLogout : undefined} />
            </div>
            {sessions.length === 0
              ? <div className="nx-empty"><NexusIcon name="Monitor" size={20} /><span>No active sessions.</span></div>
              : <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                  <thead><tr>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>Session ID</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600 }}>Created</th>
                  </tr></thead>
                  <tbody>{sessions.map(s => (
                    <tr key={s.id} className="nx-tr">
                      <td><span className="nx-cell is-mono" style={{ fontSize: 11 }}>{s.id.slice(0, 16)}…</span></td>
                      <td><span className="nx-muted">{fmt(s.createdAt)}</span></td>
                    </tr>
                  ))}</tbody>
                </table></div></div>
            }
          </div>
        )}

        {/* AUDIT */}
        {tab === 'audit' && auditContent}

      </div>

      {/* Change Role modal */}
      {editRole && (
        <TenancyModal title="Change Platform Role" icon="ShieldCheck" onClose={() => setEditRole(false)}>
          <div className="nx-form" style={{ gap: 8 }}>
            <div className="nx-form-row">
              <label>Role</label>
              <select className="nx-input" value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
                <option value="">— Select Role —</option>
                <optgroup label="System Roles">
                  {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </optgroup>
                {customRoles.length > 0 && (
                  <optgroup label="Custom Roles">
                    {customRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </optgroup>
                )}
              </select>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setEditRole(false)}>Cancel</Btn>
            <Btn variant="primary" icon="ShieldCheck" disabled={!selectedRole} onClick={handleRoleChange}>Apply Role</Btn>
          </div>
        </TenancyModal>
      )}

      {/* Suspend Access confirm */}
      {suspendConfirm && (
        <TenancyModal title={'Suspend Access — ' + user.name} icon="AlertCircle" onClose={() => setSuspendConfirm(false)}>
          <p className="nx-confirm-desc">
            This will temporarily prevent <strong>{user.name}</strong> from accessing URSA.
            Their role and account will be preserved. You can reactivate them at any time.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setSuspendConfirm(false)}>Cancel</Btn>
            <Btn variant="warning" icon="AlertCircle" data-testid="pud-suspend-confirm-btn" onClick={handleSuspend}>Suspend Access</Btn>
          </div>
        </TenancyModal>
      )}

      {/* Remove Access confirm */}
      {deleteConfirm && (
        <TenancyModal title={'Remove Access — ' + user.name} icon="ShieldOff" danger onClose={() => setDeleteConfirm(false)}>
          <p className="nx-confirm-desc">
            This will remove <strong>{user.name}</strong>'s platform role/access. Their user account
            will remain in URSA and can be assigned another role later. This is not account deletion.
            Their organization memberships are not affected.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setDeleteConfirm(false)}>Cancel</Btn>
            <Btn variant="danger" icon="ShieldOff" data-testid="pud-remove-confirm-btn" onClick={handleDelete}>Remove Access</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
